import { Box, Button, TextField, Typography } from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import Modal from '../../Unknown/Modal'
import translate from '../../../i18n/translate'

type AdminCommentModalProps = {
  visible: boolean
  initialValue?: string
  title: ReactElement
  onClose: () => void
  onSubmit: (value: string) => Promise<void> | void
  descriptionId?: string
  placeholderId?: string
  emptyErrorId?: string
  saveLabelId?: string
}

export default function AdminCommentModal({
  visible,
  initialValue = '',
  title,
  onClose,
  onSubmit,
  descriptionId = 'adminCommentVisibilityHint',
  placeholderId = 'adminCommentPlaceholder',
  emptyErrorId = 'adminCommentEmptyError',
  saveLabelId = 'saveAdminComment',
}: AdminCommentModalProps) {
  const intl = useIntl()
  const [value, setValue] = useState<string>(initialValue)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (visible) {
      setValue(initialValue)
      setError(null)
    }
  }, [visible, initialValue])

  const handleSubmit = async () => {
    const trimmed = value.trim()

    if (!trimmed) {
      setError(intl.formatMessage({ id: emptyErrorId }))
      return
    }

    try {
      setSubmitting(true)
      await onSubmit(trimmed)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={title}
      visible={visible}
      style={{ maxWidth: '500px' }}
      setVisible={onClose as any}
    >
      <Box
        my={2}
        p={2}
        display='flex'
        flexDirection='column'
        justifyContent='center'
        width={1}
        alignItems='center'
        gap={2}
      >
        {descriptionId ? (
          <Typography variant='body2' sx={{ color: '#666', fontStyle: 'italic', width: '100%' }}>
            {intl.formatMessage({ id: descriptionId })}
          </Typography>
        ) : null}
        <TextField
          variant='outlined'
          color='warning'
          multiline
          rows={4}
          fullWidth
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            if (error) setError(null)
          }}
          placeholder={intl.formatMessage({ id: placeholderId })}
          error={!!error}
          helperText={error || ''}
          dir='ltr'
        />
        <Box
          mt={2}
          display='flex'
          flexDirection='column'
          justifyContent='center'
          width={1}
          alignItems='center'
          gap={1}
        >
          <Button
            size='large'
            variant='outlined'
            color='warning'
            fullWidth
            onClick={onClose}
            disabled={submitting}
          >
            {translate('cancel')}
          </Button>
          <Button
            size='large'
            variant='contained'
            color='warning'
            fullWidth
            onClick={handleSubmit}
            disabled={submitting}
          >
            {translate(saveLabelId)}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
